import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  Wrench,
} from "@/components/icons";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import StatCard from "../../components/StatCard";
import { AiRevenueCard } from "../../components/admin/AiRevenueCard";

export default function AdminOverview() {
  const [s, setS] = useState(null);
  const [orphan, setOrphan] = useState({ total: 0, repairable: 0 });
  const [repairing, setRepairing] = useState(false);
  const {
    pharmacyEnabled,
    chatEnabled,
    supportEnabled,
    aiEnabled,
    applyFlags,
  } = useSettings();
  const [saving, setSaving] = useState(null);

  const loadOrphan = async () => {
    try {
      const { data } = await api.get("/patients/admin/orphan-stats");
      setOrphan(data || { total: 0, repairable: 0 });
    } catch {
      /* silencioso: no queremos bloquear el dashboard por esto */
    }
  };
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    loadOrphan();
  }, []);

  const runRepair = async () => {
    if (orphan.repairable === 0) {
      toast.info("No hay pacientes huérfanos que reparar automáticamente.");
      return;
    }
    if (
      !window.confirm(
        `Se asignará la oficina única a ${orphan.repairable} paciente${
          orphan.repairable === 1 ? "" : "s"
        } huérfano${orphan.repairable === 1 ? "" : "s"}. ¿Continuar?`,
      )
    )
      return;
    setRepairing(true);
    try {
      const { data } = await api.post("/patients/admin/backfill-office-id");
      toast.success(
        `Reparados: ${data.total} paciente${data.total === 1 ? "" : "s"}`,
      );
      await loadOrphan();
    } catch {
      toast.error("No se pudo ejecutar la reparación.");
    } finally {
      setRepairing(false);
    }
  };

  if (!s) return <div>Cargando…</div>;

  const toggleFlag = async (key, currentValue, label) => {
    setSaving(key);
    try {
      const newValue = !currentValue;
      const { data } = await api.put("/admin/settings", { [key]: newValue });
      applyFlags(data);
      toast.success(
        newValue ? `${label} activado` : `${label} ocultado del sistema`
      );
    } catch {
      toast.error("No se pudo actualizar la configuración");
    } finally {
      setSaving(null);
    }
  };

  const MODULES = [
    { key: "pharmacy_enabled", value: pharmacyEnabled, label: "Módulo Farmacia" },
    { key: "chat_enabled", value: chatEnabled, label: "Chat interno" },
    { key: "support_enabled", value: supportEnabled, label: "Soporte" },
    { key: "ai_enabled", value: aiEnabled, label: "ConsuDoctor AI" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#163144] tracking-tight">
            Resumen general
          </h1>
          <p className="text-sm text-[#1B405B]/70">
            Visión completa de la plataforma ConsuDoctor.
          </p>
        </div>

        {/* Module toggles */}
        <div
          className="glass rounded-[28px] p-4"
          data-testid="modules-toggle-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <SettingsIcon className="w-4 h-4 text-[#1B405B]" />
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#1B405B]/70">
              Módulos del sistema
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 min-w-[280px]">
            {MODULES.map((m) => (
              <div key={m.key} className="flex items-center justify-between gap-2">
                <div className="text-xs font-medium text-[#163144]">
                  {m.label}
                </div>
                <button
                  onClick={() => toggleFlag(m.key, m.value, m.label)}
                  disabled={saving === m.key}
                  data-testid={`module-toggle-${m.key}`}
                  title={m.value ? `Ocultar ${m.label}` : `Mostrar ${m.label}`}
                  className={`relative w-10 h-6 rounded-full transition flex-shrink-0 ${
                    m.value ? "bg-[#01BC8E]" : "bg-[#1B405B]/30"
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                      m.value ? "left-5" : "left-1"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AiRevenueCard />

      <div
        className="bg-white rounded-[28px] border border-black/[0.06] p-5 flex items-center justify-between gap-4"
        data-testid="admin-maintenance-card"
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
              orphan.total > 0
                ? "bg-amber-100 text-amber-700"
                : "bg-blue-50 text-[#019A75]"
            }`}
          >
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#1B405B]/70">
              Mantenimiento de datos
            </div>
            <div className="text-sm font-bold text-[#163144]">
              Pacientes sin oficina asignada
            </div>
            <div className="text-xs text-black/60 mt-0.5">
              {orphan.total === 0 ? (
                <>Todo en orden — cada paciente tiene su oficina.</>
              ) : (
                <>
                  <span className="font-bold text-amber-700">
                    {orphan.total}
                  </span>{" "}
                  paciente{orphan.total === 1 ? "" : "s"} huérfano
                  {orphan.total === 1 ? "" : "s"}.{" "}
                  <span className="font-bold text-[#019A75]">
                    {orphan.repairable}
                  </span>{" "}
                  se pueden reparar automáticamente (doctor con una sola oficina).
                  {orphan.repairable < orphan.total && (
                    <>
                      {" "}El resto ({orphan.total - orphan.repairable}) requiere
                      asignación manual porque el doctor tiene varias oficinas.
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={runRepair}
          disabled={repairing || orphan.repairable === 0}
          data-testid="admin-repair-orphans-btn"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border transition whitespace-nowrap ${
            orphan.repairable === 0
              ? "bg-[#F5F5F5] border-transparent text-black/40 cursor-not-allowed"
              : "bg-[#01BC8E] border-[#01BC8E] text-white hover:bg-[#019976]"
          }`}
        >
          <Wrench className="w-3.5 h-3.5" />
          {repairing
            ? "Reparando…"
            : orphan.repairable === 0
              ? "Nada por reparar"
              : `Reparar huérfanos (${orphan.repairable})`}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
        <StatCard
          title="Doctores"
          value={s.doctors}
          subtitle="Activos"
          to="/admin/usuarios"
          testid="stat-doctors"
        />
        <StatCard
          title="Secretarias"
          value={s.secretaries}
          subtitle="Registradas"
          to="/admin/usuarios"
          testid="stat-secretaries"
        />
        <StatCard
          title="Farmacias"
          value={s.pharmacies}
          subtitle="Conectadas"
          to="/admin/usuarios"
          testid="stat-pharmacies"
        />
        <StatCard
          title="Pacientes"
          value={s.patients}
          subtitle="En la plataforma"
          testid="stat-patients"
        />
        <StatCard
          title="Citas totales"
          value={s.appointments}
          subtitle="Histórico"
          testid="stat-appointments"
        />
        <StatCard
          title="Recetas emitidas"
          value={s.prescriptions}
          subtitle="Histórico"
          testid="stat-prescriptions"
        />
        <StatCard
          title="Comisión plataforma"
          value={`$${s.total_platform_commission.toFixed(2)}`}
          subtitle="Acumulada"
          to="/admin/comisiones"
          active
          testid="stat-commission"
        />
        <StatCard
          title="Pendiente farmacias"
          value={`$${s.pending_pharmacy_payments.toFixed(2)}`}
          subtitle="Por cobrar"
          to="/admin/pagos-farmacia"
          testid="stat-pending-pharmacy"
        />
      </div>

      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#1B405B]/70">
              Comisión por día
            </div>
            <div className="text-xl font-bold text-[#163144]">
              Evolución de ingresos plataforma
            </div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={s.platform_commission_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1B405B22" />
              <XAxis dataKey="date" stroke="#163144" fontSize={12} />
              <YAxis stroke="#163144" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: "rgba(255,255,255,0.9)",
                  border: "1px solid #1B405B22",
                  borderRadius: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="amount"
                stroke="#1B405B"
                strokeWidth={3}
                dot={{ r: 4, fill: "#1B405B" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
